// Authentication context for session management with Cognito
// Requirements: 8.1, 8.2, 8.3, 8.4

import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, User } from '../types/auth';
import { CognitoAuthService } from '../services/cognitoAuthService';
import { configureCognito, validateCognitoConfig } from '../config/cognito';

// Action types for auth reducer
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string; refreshToken: string } }
  | { type: 'AUTH_FAILURE'; payload: { error: string } }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'TOKEN_REFRESH_START' }
  | { type: 'TOKEN_REFRESH_SUCCESS'; payload: { token: string; refreshToken: string } }
  | { type: 'TOKEN_REFRESH_FAILURE' }
  | { type: 'CLEAR_ERROR' };

// Initial auth state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  refreshToken: null,
  loading: false,
  error: null
};

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        loading: false,
        error: null
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        loading: false,
        error: action.payload.error
      };

    case 'AUTH_LOGOUT':
      return {
        ...initialState
      };

    case 'TOKEN_REFRESH_START':
      return {
        ...state,
        loading: true
      };

    case 'TOKEN_REFRESH_SUCCESS':
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        loading: false,
        error: null
      };

    case 'TOKEN_REFRESH_FAILURE':
      return {
        ...initialState,
        error: 'Session expired. Please log in again.'
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };

    default:
      return state;
  }
};

// Auth context interface
interface AuthContextType {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  forceLogout: () => Promise<void>;
}

// Create auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize Cognito configuration and auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      // Configure Cognito
      if (validateCognitoConfig()) {
        configureCognito();
      } else {
        console.error('Cognito configuration is invalid. Authentication will not work properly.');
        return;
      }

      const token = CognitoAuthService.getToken();
      const user = CognitoAuthService.getCurrentUser();
      const refreshToken = CognitoAuthService.getRefreshToken();

      if (token && user && refreshToken) {
        // Validate token with Cognito
        const isValid = await CognitoAuthService.validateToken();
        
        if (isValid) {
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user, token, refreshToken }
          });
        } else {
          // Token is invalid, clear auth data
          await CognitoAuthService.logout();
        }
      }
    };

    initializeAuth();
  }, []);

  // Setup automatic token refresh
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;

    if (state.isAuthenticated && state.token) {
      // Refresh token every 50 minutes (tokens expire in 1 hour)
      refreshInterval = setInterval(async () => {
        try {
          await refreshToken();
        } catch (error) {
          console.error('Automatic token refresh failed:', error);
          // Force logout on refresh failure
          await logout();
        }
      }, 50 * 60 * 1000); // 50 minutes
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [state.isAuthenticated, state.token]);

  // Login function
  const login = async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });

    try {
      const authResult = await CognitoAuthService.login({ email, password });
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: authResult.user,
          token: authResult.access_token,
          refreshToken: authResult.refresh_token
        }
      });
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle specific authentication errors
      let errorMessage = error.message || 'Login failed';
      
      if (error.message?.includes('There is already a signed in user')) {
        errorMessage = 'Authentication conflict detected. Please try again.';
        
        // Try to force logout and clear all auth state
        try {
          await CognitoAuthService.forceLogout();
        } catch (logoutError) {
          console.error('Force logout failed:', logoutError);
        }
      }
      
      dispatch({
        type: 'AUTH_FAILURE',
        payload: { error: errorMessage }
      });
      throw new Error(errorMessage);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      await CognitoAuthService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // Force logout function - clears all auth state
  const forceLogout = async (): Promise<void> => {
    try {
      await CognitoAuthService.forceLogout();
    } catch (error) {
      console.error('Force logout error:', error);
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // Refresh token function
  const refreshToken = async (): Promise<void> => {
    dispatch({ type: 'TOKEN_REFRESH_START' });

    try {
      const authResult = await CognitoAuthService.refreshToken();
      
      dispatch({
        type: 'TOKEN_REFRESH_SUCCESS',
        payload: {
          token: authResult.access_token,
          refreshToken: authResult.refresh_token
        }
      });
    } catch (error: any) {
      console.error('Token refresh failed:', error);
      dispatch({ type: 'TOKEN_REFRESH_FAILURE' });
      throw error;
    }
  };

  // Clear error function
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const contextValue: AuthContextType = {
    state,
    login,
    logout,
    refreshToken,
    clearError,
    forceLogout
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;